import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { isManufacturerOrBODPill } from 'components/WorkspaceEditor/utils'
import { TrackChangeType } from 'models/changes'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { shouldSkipForKeyColonSpacing } from './PlainText/utils/textUtils'

/** Context shared by the hide-checks NodeContentRenderer applies per content item. */
export interface ContentItemVisibilityContext {
    node: DataNode
    isProductType: boolean
    areTrackChangesVisible: boolean
    isInactiveContentVisible: boolean
}

/** Don't render deleted content when track changes are not visible. */
export const shouldHideDeleted = (
    change: string,
    areTrackChangesVisible: boolean
): boolean => change === TrackChangeType.DELETED && !areTrackChangesVisible

/** Hides empty manufacturer/BOD placeholder pills (blank or just '.'), except OptionGroups. */
export const shouldHideEmptyManufacturer = (
    contentItem: Partial<TextContentElement>,
    node: DataNode
): boolean => {
    const isOptionGroup = contentItem.type === NODE_TYPES.OptionGroup
    const isManufacturerOrBOD = isManufacturerOrBODPill(node)
    const isEmpty =
        contentItem.text?.trim() === '' || contentItem.text?.trim() === '.'

    return isManufacturerOrBOD && !isOptionGroup && isEmpty
}

/**
 * Determines whether a content item should be skipped from rendering entirely —
 * combines every individual hide rule NodeContentRenderer applies per item.
 */
export const isContentItemHidden = (
    contentItem: Partial<TextContentElement>,
    index: number,
    contentItemNodes: Partial<TextContentElement>[],
    context: ContentItemVisibilityContext
): boolean => {
    // Existing data fix [2556847]: Product Types created from the Insert Drawing Designation tag
    // generated an inline IDD text field, resulting in a duplicate — one inline, one as the default.
    // This hides the inline text field and the accompanying duplicate colon.
    const isLegacyDrawingDesignationDuplicateColon =
        (contentItem?.elements?.[0]?.placeholderText ===
            'Insert Drawing Designation' &&
            index === 1) ||
        contentItem?.text?.trim() === ':'
    if (isLegacyDrawingDesignationDuplicateColon) return true

    const shouldHideInactiveContent =
        !context.isInactiveContentVisible &&
        contentItem.optionItemState === 'InActive'

    return (
        shouldHideEmptyManufacturer(contentItem, context.node) ||
        // Don't render deleted content when track changes are not visible
        shouldHideDeleted(
            contentItem?.changeType?.toUpperCase() ?? '',
            context.areTrackChangesVisible
        ) ||
        // Don't render inactive options when inactive content visibility is off
        shouldHideInactiveContent ||
        // Skip duplicate colon after FieldRef or whitespace before FieldRef/colon
        shouldSkipForKeyColonSpacing(
            contentItem,
            index,
            contentItemNodes,
            context.isProductType
        )
    )
}

/**
 * Per-item visibility (true = should render), computed once for the whole list so
 * callers don't re-run isContentItemHidden per item multiple times (e.g. once to find
 * the first visible index and again per item in a render loop).
 */
export const getVisibilityFlags = (
    contentItemNodes: Partial<TextContentElement>[],
    context: ContentItemVisibilityContext
): boolean[] =>
    contentItemNodes.map(
        (contentItem, index) =>
            !isContentItemHidden(contentItem, index, contentItemNodes, context)
    )
